import { todayWithSystemTimeZone } from '@magicdoorinc/date';
import { createMutation, createTriggerResource } from '@magicdoorinc/ui';
import { createEffect, createSignal, onCleanup } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { LeaseRecurringChargeRepository } from '~/repositories/leaseRecurringChargeRepository';
import { insuranceRepo } from '~/repositories/settings/insuranceRepository';
import { LeaseRecurringChargeStatus } from '~/swagger/portal/Api';
import type { GetLeaseRecurringChargeParamsInput } from '~/repositories/leaseRecurringChargeRepository';
import type { CreateRecurringLeaseChargeDto, UpdateRecurringLeaseChargeDto } from '~/swagger/portal/Api';

export const [LeaseRentChargesProvider, useLeaseRentCharges] = createMagicDoorContext('LeaseRentCharges', () => {
  const repo = new LeaseRecurringChargeRepository();
  const [currentLeaseId, setCurrentLeaseId] = createSignal<string | null>(null);

  const [recurringCharges, getRecurringCharges, { refetch: refetchRecurringCharges }] = createTriggerResource(
    async (params: GetLeaseRecurringChargeParamsInput) => {
      const leaseIdStr = String(params.leaseId);

      setCurrentLeaseId(leaseIdStr);
      const [chargesResponse, damageWaiverSettings] = await Promise.all([
        repo.getRecurringCharges(params),
        insuranceRepo.getLeaseDamageWaiverSettings(leaseIdStr).catch(() => null),
      ]);

      const charges = chargesResponse.recurringCharges || [];

      // Inject Damage Waiver charge if applicable
      if (
        damageWaiverSettings &&
        !damageWaiverSettings.isOptedOut &&
        damageWaiverSettings.nextChargeDate &&
        damageWaiverSettings.effectiveMonthlyFee > 0
      ) {
        charges.push({
          id: `dw-${leaseIdStr}`,
          description: 'Damage waiver',
          amount: damageWaiverSettings.effectiveMonthlyFee,
          chargeFrequency: 'monthly',
          status: LeaseRecurringChargeStatus.Active,
          startFrom: damageWaiverSettings.policyStartDate || todayWithSystemTimeZone(),
          nextCharge: damageWaiverSettings.nextChargeDate,
          chartOfAccount: damageWaiverSettings.recurringLeaseChargeChartOfAccount,
          isSystem: true,
        } as any);
      }

      return charges;
    }
  );

  createEffect(() => {
    const handleDWSettingsChanged = (event: CustomEvent) => {
      const leaseId = currentLeaseId();
      if (leaseId && event.detail?.leaseId === leaseId) {
        refetchRecurringCharges();
      }
    };

    window.addEventListener('DWSettingsChanged', handleDWSettingsChanged as EventListener);
    onCleanup(() => {
      window.removeEventListener('DWSettingsChanged', handleDWSettingsChanged as EventListener);
    });
  });

  const addRecurringCharge = createMutation(async (params: { leaseId: string; payload: CreateRecurringLeaseChargeDto }) => {
    const response = await repo.addRecurringCharge(params.leaseId, params.payload);
    refetchRecurringCharges();
    return response;
  });

  const updateRecurringCharge = createMutation(
    async (params: { leaseId: string; recurringId: string; payload: UpdateRecurringLeaseChargeDto }) => {
      await repo.updateRecurringCharge(params.leaseId, params.recurringId, params.payload);
      refetchRecurringCharges();
    }
  );

  const deleteRecurringCharge = createMutation(async (params: { leaseId: string; recurringId: string; deletePostedCharges: boolean }) => {
    await repo.deleteRecurringCharge(params.leaseId, params.recurringId, params.deletePostedCharges);
    refetchRecurringCharges();
  });

  return {
    recurringCharges,
    getRecurringCharges,
    addRecurringCharge,
    updateRecurringCharge,
    deleteRecurringCharge,
  };
});
